/**
 * Catalog data hooks.
 *
 * Every key includes the active language, because the server resolves display
 * text per request (see `lib/queryKeys.ts`).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { del, get, patch, post, toQueryParams } from '@/lib/api'
import { queryKeys, queryScopes } from '@/lib/queryKeys'
import type {
  Author,
  BookDetail,
  BookFilters,
  BookListItem,
  CatalogStats,
  CategoryDetail,
  CategoryNode,
  CategoryTreeResponse,
  Paginated,
  SuggestResponse,
} from '@/types/api'

import { useLanguage } from './useDirection'

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
export const useCategoryTree = () => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.categoryTree(language),
    queryFn: () => get<CategoryTreeResponse>('/categories/tree/'),
    // The tree changes only when staff curate it.
    staleTime: 5 * 60_000,
  })
}

export const useCategory = (slug: string | undefined) => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.category(slug ?? '', language),
    queryFn: () => get<CategoryDetail>(`/categories/${slug}/`),
    enabled: Boolean(slug),
  })
}

/** Flatten the tree for use in select boxes and parent pickers. */
export const flattenTree = (
  nodes: CategoryNode[],
  depth = 0,
): { node: CategoryNode; depth: number }[] =>
  nodes.flatMap((node) => [
    { node, depth },
    ...flattenTree(node.children, depth + 1),
  ])

/** Locate a node anywhere in the tree by slug. */
export const findNode = (nodes: CategoryNode[], slug: string): CategoryNode | undefined => {
  for (const node of nodes) {
    if (node.slug === slug) return node
    const found = findNode(node.children, slug)
    if (found) return found
  }
  return undefined
}

/** Slugs of every ancestor of `slug`, so the tree can auto-expand to it. */
export const ancestorSlugs = (nodes: CategoryNode[], slug: string): string[] => {
  const walk = (list: CategoryNode[], trail: string[]): string[] | null => {
    for (const node of list) {
      if (node.slug === slug) return trail
      const deeper = walk(node.children, [...trail, node.slug])
      if (deeper) return deeper
    }
    return null
  }
  return walk(nodes, []) ?? []
}

// ---------------------------------------------------------------------------
// Books
// ---------------------------------------------------------------------------
export const useBooks = (filters: BookFilters) => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.books(filters, language),
    queryFn: () =>
      get<Paginated<BookListItem>>(
        `/books/?${toQueryParams(filters as Record<string, unknown>).toString()}`,
      ),
    // Keeps the previous page visible while the next one loads, so paging and
    // filtering do not flash an empty grid.
    placeholderData: keepPreviousData,
  })
}

export const useBook = (slug: string | undefined) => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.book(slug ?? '', language),
    queryFn: () => get<BookDetail>(`/books/${slug}/`),
    enabled: Boolean(slug),
  })
}

export const useRecentBooks = () => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.recentBooks(language),
    queryFn: () => get<BookListItem[]>('/books/recent/'),
    staleTime: 2 * 60_000,
  })
}

export const useCatalogStats = () => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.catalogStats(language),
    queryFn: () => get<CatalogStats>('/stats/'),
    staleTime: 5 * 60_000,
  })
}

/** Type-ahead suggestions. `term` should already be debounced by the caller. */
export const useSuggestions = (term: string) => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.suggestions(term, language),
    queryFn: () => get<SuggestResponse>(`/search/suggest/?q=${encodeURIComponent(term)}`),
    enabled: term.trim().length >= 2,
    staleTime: 30_000,
  })
}

export const useAuthors = () => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.authors(language),
    queryFn: () => get<Paginated<Author>>('/authors/?page_size=200'),
    staleTime: 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Staff mutations
// ---------------------------------------------------------------------------
export const useSaveBook = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ slug, payload }: { slug?: string; payload: Record<string, unknown> }) =>
      slug ? patch<BookDetail>(`/books/${slug}/`, payload) : post<BookDetail>('/books/', payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryScopes.books })
      void client.invalidateQueries({ queryKey: queryScopes.book })
      void client.invalidateQueries({ queryKey: queryScopes.categoryTree })
    },
  })
}

export const useDeleteBook = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (slug: string) => del(`/books/${slug}/`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryScopes.books })
      void client.invalidateQueries({ queryKey: queryScopes.categoryTree })
    },
  })
}

export const useSaveCategory = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ slug, payload }: { slug?: string; payload: Record<string, unknown> }) =>
      slug
        ? patch<CategoryDetail>(`/categories/${slug}/`, payload)
        : post<CategoryDetail>('/categories/', payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryScopes.categoryTree })
    },
  })
}

export const useMoveCategory = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      slug,
      target,
      position,
    }: {
      slug: string
      target: number | null
      position: 'first-child' | 'last-child' | 'left' | 'right'
    }) => post<CategoryDetail>(`/categories/${slug}/move/`, { target, position }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryScopes.categoryTree })
      void client.invalidateQueries({ queryKey: queryScopes.books })
    },
  })
}

export const useDeleteCategory = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (slug: string) => del(`/categories/${slug}/`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryScopes.categoryTree })
    },
  })
}
